import {FC} from "react";
import {styled} from "@mui/material/styles";
import {useAppSelector} from "../../../hooks/redux";
import {selectCurrentCompany} from "../../../features/companies/model/selectors";
import {t} from "../../../styles/theme/tokens";

/**
 * ============================================================================
 * CompanyIndicator — название текущей компании в подвале sidebar'а.
 * ============================================================================
 *
 * Зачем: у части клиентов несколько компаний, и им нужно понимать, в контексте
 * какой компании они сейчас работают. Для большинства это просто фоновая
 * подпись.
 *
 * Поведение сейчас: read-only. Просто показываем `company.name`.
 *
 * Задел на будущее (переключение компаний):
 *   Корень — styled("div"). Когда понадобится переключатель, меняем тег на
 *   "button", добавляем onClick + hover-состояние (action.hover) + иконку
 *   expand_more справа. MenuPanel при этом трогать не придётся — он просто
 *   рендерит <CompanyIndicator/>.
 *
 * Дизайн (по DESIGN_SYSTEM):
 *   - Размер caption (12px), цвет text.secondary — это контекст, не заголовок.
 *   - Одна строка, noWrap + ellipsis: названия бывают длинными
 *     («ООО Транспортная компания Северо-Запад»), а ширина sidebar 240px.
 *   - Без моно-шрифта и tabular-nums: это название, а не числовые данные.
 *   - Без хинта «Компания:» сверху — лишний шум, название говорит само за себя.
 *   - Цвета только через токены (t.*), без хардкода.
 *
 * Если текущей компании нет (не загрузилась / гость) — не рендерим ничего,
 * чтобы не показывать пустую строку.
 * ============================================================================
 */

const Root = styled("div")({
    minWidth: 0,                 // обязательно для работы ellipsis во flex-родителе
    width: "100%",
    paddingInline: 4,
    fontSize: "0.75rem",         // caption — 12px
    lineHeight: 1.4,
    fontWeight: 500,
    color: t.text.secondary,
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
    userSelect: "none",
});

const CompanyIndicator: FC = () => {
    const company = useAppSelector(selectCurrentCompany);

    const name = company?.name?.trim();
    if (!name) return null;

    // title даёт полное название по наведению — полезно, когда оно обрезано.
    return <Root title={name}>{name}</Root>;
};

export default CompanyIndicator;